import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Typography, Grid, Paper, Card, CardContent, Button, Chip, Stack, Avatar,
} from '@mui/material';
import {
  Bookmark, Payment, SupportAgent, Home, TrendingUp, Group,
  ArrowForward, CheckCircle, Schedule, Warning, Description, Gavel, Business,
} from '@mui/icons-material';
import useAuthStore from '../../store/useStore';
import { bookingsAPI, paymentsAPI, ticketsAPI, tenancyAPI, disputesAPI, propertiesAPI } from '../../api/client';

function StatCard({ icon, title, value, color, link }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>{title}</Typography>
            <Typography variant="h4" fontWeight={700}>{value}</Typography>
          </Box>
          <Avatar sx={{ bgcolor: `${color}.light`, color: `${color}.main`, width: 48, height: 48 }}>
            {icon}
          </Avatar>
        </Stack>
        {link && (
          <Button component={Link} to={link} size="small" endIcon={<ArrowForward />} sx={{ mt: 1.5 }}>
            View All
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function DashboardPage() {
  const { user } = useAuthStore();
  const [stats, setStats] = useState({ bookings: 0, payments: 0, tickets: 0, tenancy: 0, disputes: 0, properties: 0, pendingBookings: 0 });
  const [recentBookings, setRecentBookings] = useState([]);

  const isLandlord = ['landlord', 'agent'].includes(user?.role);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const promises = [
          bookingsAPI.list({ page: 1, per_page: 5 }),
          paymentsAPI.list({ page: 1, per_page: 1 }),
          ticketsAPI.list({ page: 1, per_page: 1 }),
          tenancyAPI.list({ page: 1, per_page: 1 }),
          disputesAPI.list({ per_page: 1 }),
        ];
        if (isLandlord) {
          promises.push(propertiesAPI.getMyProperties({ page: 1, per_page: 1 }));
        }
        const results = await Promise.allSettled(promises);
        const bookingsRes = results[0].status === 'fulfilled' ? results[0].value : null;
        const paymentsRes = results[1].status === 'fulfilled' ? results[1].value : null;
        const ticketsRes = results[2].status === 'fulfilled' ? results[2].value : null;
        const tenancyRes = results[3].status === 'fulfilled' ? results[3].value : null;
        const disputesRes = results[4].status === 'fulfilled' ? results[4].value : null;
        const propertiesRes = results[5]?.status === 'fulfilled' ? results[5].value : null;

        const allBookings = bookingsRes?.data?.bookings || [];
        const pendingCount = allBookings.filter(b => b.status === 'pending').length;

        setStats({
          bookings: bookingsRes?.data?.total || 0,
          payments: paymentsRes?.data?.total || 0,
          tickets: ticketsRes?.data?.total || 0,
          tenancy: tenancyRes?.data?.total || 0,
          disputes: disputesRes?.data?.disputes?.length || 0,
          properties: propertiesRes?.data?.total || 0,
          pendingBookings: pendingCount,
        });
        setRecentBookings(allBookings);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      }
    };
    fetchData();
  }, [isLandlord]);

  const statusColors = {
    pending: 'warning', confirmed: 'success', cancelled: 'error', completed: 'info',
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700}>
          Welcome back, {user?.first_name}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Here's what's happening with your account.
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {isLandlord ? (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard icon={<Business />} title="My Properties" value={stats.properties} color="primary" link="/dashboard/properties" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard icon={<Bookmark />} title="Bookings" value={stats.bookings} color="info" link="/dashboard/bookings" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard icon={<Description />} title="Tenancy Agreements" value={stats.tenancy} color="secondary" link="/dashboard/tenancy" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard icon={<Payment />} title="Payments" value={stats.payments} color="success" link="/dashboard/payments" />
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard icon={<Bookmark />} title="Total Bookings" value={stats.bookings} color="primary" link="/dashboard/bookings" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard icon={<Payment />} title="Payments" value={stats.payments} color="success" link="/dashboard/payments" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard icon={<SupportAgent />} title="Support Tickets" value={stats.tickets} color="warning" link="/dashboard/tickets" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<Group />}
                title="Room Pairing"
                value="Find Roommate"
                color="secondary"
                link="/dashboard/room-pairing"
              />
            </Grid>
          </>
        )}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>Recent Bookings</Typography>
              <Button component={Link} to="/dashboard/bookings" size="small" endIcon={<ArrowForward />}>View All</Button>
            </Box>
            {recentBookings.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No bookings yet</Typography>
                <Button component={Link} to="/properties" variant="contained" sx={{ mt: 2 }}>Browse Properties</Button>
              </Box>
            ) : (
              <Stack spacing={2}>
                {recentBookings.map((booking) => (
                  <Paper key={booking.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography fontWeight={600}>Booking #{booking.id.slice(0, 8)}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(booking.check_in_date).toLocaleDateString()} - {new Date(booking.check_out_date).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Box textAlign="right">
                        <Chip label={booking.status} size="small" color={statusColors[booking.status] || 'default'} sx={{ textTransform: 'capitalize' }} />
                        <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                          GHS {booking.total_amount?.toLocaleString()}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Quick Actions</Typography>
            <Stack spacing={1.5}>
              {isLandlord ? (
                <>
                  <Button component={Link} to="/dashboard/properties/create" variant="outlined" fullWidth startIcon={<Business />}>Add Property</Button>
                  <Button component={Link} to="/dashboard/bookings" variant="outlined" fullWidth startIcon={<Bookmark />}>Manage Bookings</Button>
                  <Button component={Link} to="/dashboard/tenancy" variant="outlined" fullWidth startIcon={<Description />}>Tenancy Agreements</Button>
                  <Button component={Link} to="/dashboard/disputes" variant="outlined" fullWidth startIcon={<Gavel />}>View Disputes</Button>
                </>
              ) : (
                <>
                  <Button component={Link} to="/properties" variant="outlined" fullWidth startIcon={<Home />}>Find Accommodation</Button>
                  <Button component={Link} to="/dashboard/room-pairing" variant="outlined" fullWidth startIcon={<Group />}>Find a Roommate</Button>
                  <Button component={Link} to="/dashboard/tickets" variant="outlined" fullWidth startIcon={<SupportAgent />}>Get Support</Button>
                  <Button component={Link} to="/dashboard/ai-assistant" variant="outlined" fullWidth color="secondary">AI Assistant</Button>
                </>
              )}
            </Stack>
          </Paper>

          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Profile Status</Typography>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                {user?.is_email_verified ? <CheckCircle color="success" /> : <Warning color="warning" />}
                <Typography variant="body2">Email {user?.is_email_verified ? 'Verified' : 'Not Verified'}</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                {user?.is_verified ? <CheckCircle color="success" /> : <Warning color="warning" />}
                <Typography variant="body2">KYC {user?.is_verified ? 'Approved' : 'Pending'}</Typography>
              </Stack>
            </Stack>
            {!user?.is_verified && (
              <Button component={Link} to="/dashboard/profile" variant="text" size="small" sx={{ mt: 1 }}>
                Complete Verification
              </Button>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default DashboardPage;
